import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import AppShell from './components/layout/AppShell';
import ErrorBoundary from './components/ErrorBoundary';
import DashboardPage from './pages/DashboardPage';
import LiveOCPage from './pages/LiveOCPage';
import TradePlanPage from './pages/TradePlanPage';
import IntradayPage from './pages/IntradayPage';
import ParticipantPage from './pages/ParticipantPage';
import EodSyncPage from './pages/EodSyncPage';
import EodImportPage from './pages/EodImportPage';
import EodChartPage from './pages/EodChartPage';
import EodOCPage from './pages/EodOCPage';
import LoginPage from './pages/LoginPage';
import { AuthProvider, useAuth } from './hooks/useAuth';
import './index.css';
import './styles/tmr-shell.css';

function EodFutPage() {
  return (
    <div className="obsidian-content">
      <h2>Futures Market (EOD)</h2>
      <p className="text-sm text-slate-400">
        Full page using FO chart API coming soon.
      </p>
    </div>
  );
}

function LiveSyncPlaceholder() {
  return (
    <div className="obsidian-content">
      <h2>Live Data Sync</h2>
      <p className="text-sm text-slate-400">
        Live sync control will move fully to Node in a later phase. EOD Sync/Import are available now.
      </p>
    </div>
  );
}

function AuthGate({ children }) {
  const { user, loading, error, refresh } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const onLogin = location.pathname === '/login';

  useEffect(() => {
    if (!loading && !user && !onLogin) {
      const next = encodeURIComponent(location.pathname + location.search);
      navigate(`/login?next=${next}`, { replace: true });
    }
  }, [loading, user, onLogin, location.pathname, location.search, navigate]);

  if (onLogin) {
    return children;
  }

  if (loading) {
    return (
      <div className="tmr-loading-screen">
        <div className="tmr-loading-orb" aria-hidden="true" />
        <div className="tmr-loading-text">Loading Tactical Market Radar…</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="tmr-loading-screen" style={{ flexDirection: 'column', gap: 12 }}>
        <div className="tmr-loading-text">
          {error === 'unauth' ? 'Redirecting to login…' : 'Session unavailable'}
        </div>
        {error && error !== 'unauth' && (
          <>
            <div style={{ fontSize: 12, color: 'var(--text-dim)', maxWidth: 360, textAlign: 'center' }}>
              Could not verify session ({error}). Check network and try again.
            </div>
            <button
              type="button"
              className="tmr-btn tmr-btn-primary"
              style={{ height: 36, padding: '0 16px', cursor: 'pointer' }}
              onClick={() => refresh()}
            >
              Retry
            </button>
            <a href="/login" style={{ fontSize: 12, color: 'var(--blue)' }}>
              Go to login
            </a>
          </>
        )}
      </div>
    );
  }

  return children;
}

function ShellRoute({ page, children, fullWidth = false }) {
  const { user } = useAuth();
  const isFull =
    fullWidth
    || page === 'dashboard'
    || page === 'live_oc'
    || page === 'live_chart'
    || page === 'eod_oc'
    || page === 'eod_chart';

  return (
    <AppShell activeKey={page} username={user?.username || 'User'} fullWidth={isFull}>
      <ErrorBoundary name={page || 'page'} key={page}>
        {children}
      </ErrorBoundary>
    </AppShell>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AuthGate>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/" element={<ShellRoute page="dashboard"><DashboardPage /></ShellRoute>} />
            <Route path="/live-oc" element={<ShellRoute page="live_oc" fullWidth><LiveOCPage /></ShellRoute>} />
            <Route path="/trade-plan" element={<ShellRoute page="trade_plan"><TradePlanPage /></ShellRoute>} />
            <Route path="/intraday" element={<ShellRoute page="live_chart" fullWidth><IntradayPage /></ShellRoute>} />
            <Route path="/eod-chart" element={<ShellRoute page="eod_chart" fullWidth><EodChartPage /></ShellRoute>} />
            <Route path="/eod-oc" element={<ShellRoute page="eod_oc" fullWidth><EodOCPage /></ShellRoute>} />
            <Route path="/eod-fut" element={<ShellRoute page="eod_fut"><EodFutPage /></ShellRoute>} />
            <Route path="/participant" element={<ShellRoute page="part_oi" fullWidth><ParticipantPage /></ShellRoute>} />
            <Route path="/eod-sync" element={<ShellRoute page="eod_sync" fullWidth><EodSyncPage /></ShellRoute>} />
            <Route path="/eod-import" element={<ShellRoute page="eod_import" fullWidth><EodImportPage /></ShellRoute>} />
            <Route path="/live-sync" element={<ShellRoute page="live_sync"><LiveSyncPlaceholder /></ShellRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthGate>
      </BrowserRouter>
    </AuthProvider>
  );
}
